import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/components/ui/popover";
import { useTranslation } from "react-i18next";

/** Common IANA zones (incl. mining regions) used when the runtime lacks Intl.supportedValuesOf. */
const FALLBACK_ZONES = [
  "UTC",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Santiago",
  "America/Lima",
  "America/Bogota",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Australia/Perth",
  "Australia/Sydney",
  "Australia/Brisbane",
  "Africa/Johannesburg",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Moscow",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Jakarta",
];

function allTimezones(): string[] {
  const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof supported === "function") {
    try {
      const zones = supported("timeZone");
      if (Array.isArray(zones) && zones.length) return zones;
    } catch {
      /* fall through */
    }
  }
  return FALLBACK_ZONES;
}

/** UTC offset like "GMT-03:00" for a zone at "now", to help operators recognise it. */
function offsetLabel(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(
      new Date(),
    );
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

export function TimezoneSelect({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string | null;
  onChange: (tz: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const zones = useMemo(allTimezones, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? zones.filter((z) => z.toLowerCase().includes(s)) : zones;
    return list.slice(0, 300);
  }, [zones, q]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>{value || placeholder || t("timezoneSelect.placeholder")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-2.5 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("timezoneSelect.searchPlaceholder")}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-6 text-center text-xs text-muted-foreground">{t("timezoneSelect.empty")}</div>
          ) : (
            filtered.map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => {
                  onChange(z);
                  setOpen(false);
                  setQ("");
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent",
                  z === value && "bg-accent",
                )}
              >
                <span className="min-w-0 flex-1 truncate">{z.replace(/_/g, " ")}</span>
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{offsetLabel(z)}</span>
                {z === value && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
